// WS0 task 18: resolves the current page to the same page in the other JIFI app, for the
// "Open in new JIFI" switch. Pure: no window, no env. A copy of next-frontend/src/lib/ws0-switch.ts;
// keep the two in step. Both are tested against scripts/parity/switch-fixtures.json. The path map
// is src/generated/route-map.json, written by scripts/parity/sync-route-map.mjs; its rule shape is
// documented in scripts/parity/extract-routes.mjs.
// Temporary: task 17 deletes the switch with frontend/.
//
// A rule may carry an optional query predicate, `query`: every key must be present in the current
// query with that exact value for the rule to match. An object, or a "k=v&k2=v2" string.

/** Query keys the switch adds for the "no equivalent page" notice; never carried onwards. */
export const SWITCH_FROM_PARAM = 'ws0_switch_from';
export const SWITCH_REASON_PARAM = 'ws0_switch_reason';
const SWITCH_PARAMS = [SWITCH_FROM_PARAM, SWITCH_REASON_PARAM];
// A path: leading slash, no second slash or backslash up front, no whitespace, bounded length.
const SAFE_FROM = /^\/(?![\/\\])\S{0,199}$/;

const NO_RULE_REASON = 'this page is not in the old/new path map';
const NO_TARGET_REASON = 'this page has no equivalent in the other app';

const stripTrailingSlash = (pathname) => (pathname.length > 1 ? pathname.replace(/\/+$/, '') || '/' : pathname);

const withoutSwitchParams = (search) => {
  const params = new URLSearchParams(search);
  if (!SWITCH_PARAMS.some((key) => params.has(key))) return search.replace(/^\?/, '');
  SWITCH_PARAMS.forEach((key) => params.delete(key));
  return params.toString();
};

const queryMatches = (predicate, search) => {
  if (!predicate) return true;
  const expected = typeof predicate === 'string' ? Object.fromEntries(new URLSearchParams(predicate)) : predicate;
  const actual = new URLSearchParams(search);
  return Object.entries(expected).every(([key, value]) => actual.get(key) === String(value));
};

// `to` may carry its own query (e.g. "/notifications?read=1"). The original query wins on a key
// clash; with no query on `to` the original string is carried verbatim.
const mergeQuery = (targetQuery, originalQuery) => {
  if (!targetQuery) return originalQuery;
  if (!originalQuery) return targetQuery;
  const merged = new URLSearchParams(targetQuery);
  const original = new URLSearchParams(originalQuery);
  new Set(original.keys()).forEach((key) => merged.delete(key));
  original.forEach((value, key) => merged.append(key, value));
  return merged.toString();
};

/**
 * Applies the first matching rule to the current location ({ pathname, search, hash }). A match
 * with a target returns { fallback: false, path } with params substituted and the query string and
 * hash carried. A null target, or no matching rule, returns { fallback: true, path, reason }: the
 * map's fallback (the other app's home) and a reason to show there.
 */
export function resolveSwitchTarget(map, location) {
  const pathname = stripTrailingSlash(location.pathname || '/');
  const search = withoutSwitchParams(location.search || '');
  const hash = location.hash && location.hash !== '#' ? location.hash.replace(/^#?/, '#') : '';

  for (const rule of map.rules) {
    const match = new RegExp(rule.regex).exec(pathname);
    if (!match || !queryMatches(rule.query, search)) continue;
    if (rule.to === null) {
      return { fallback: true, path: map.fallback, reason: rule.reason || NO_TARGET_REASON };
    }
    const groups = match.groups || {};
    const queryAt = rule.to.indexOf('?');
    const targetPath = queryAt === -1 ? rule.to : rule.to.slice(0, queryAt);
    const targetQuery = queryAt === -1 ? '' : rule.to.slice(queryAt + 1);
    const resolvedPath = targetPath.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (token, name) => groups[name] ?? token);
    const query = mergeQuery(targetQuery, search);
    return { fallback: false, path: `${resolvedPath}${query ? `?${query}` : ''}${hash}` };
  }

  return { fallback: true, path: map.fallback, reason: NO_RULE_REASON };
}

/** The full URL in the other app: `baseUrl` + target, plus the notice params on a fallback. */
export function buildSwitchHref(baseUrl, target, fromPathname) {
  const base = baseUrl.replace(/\/+$/, '');
  if (!target.fallback) return `${base}${target.path}`;
  // Only the path travels: the notice text is fixed, so a crafted link cannot put words in it.
  const params = new URLSearchParams({ [SWITCH_FROM_PARAM]: fromPathname });
  return `${base}${target.path}?${params.toString()}`;
}

/**
 * Reads the notice a fallback switch left in the URL. Returns the one-line message to show (or
 * null) and the query string with the notice params removed, so it shows once.
 */
export function readSwitchNotice(search) {
  const params = new URLSearchParams(search);
  const reason = params.get(SWITCH_REASON_PARAM);
  const from = params.get(SWITCH_FROM_PARAM);
  if (reason === null && from === null) return { message: null, search: search.replace(/^\?/, '') };
  SWITCH_PARAMS.forEach((key) => params.delete(key));
  // Anyone can craft this link, so show only something shaped like a JIFI path, and never free text.
  const page = from && SAFE_FROM.test(from) ? `${from} ` : 'that page ';
  return {
    message: `Opened the home page instead: ${page}has no equivalent here.`,
    search: params.toString(),
  };
}
